import type { WikoRpcSubscriptionsApi, WikoRpcSubscriptionsApiUnstable } from '@wiko/rpc-subscriptions-api';
import { createWikoRpcSubscriptionsApi } from '@wiko/rpc-subscriptions-api';
import {
    createSubscriptionRpc,
    RpcSubscriptionsApiMethods,
    type RpcSubscriptionsTransport,
} from '@wiko/rpc-subscriptions-spec';
import { ClusterUrl } from '@wiko/rpc-types';

import { DEFAULT_RPC_SUBSCRIPTIONS_CONFIG } from './rpc-default-config';
import {
    createDefaultWikoRpcSubscriptionsChannelCreator,
    DefaultRpcSubscriptionsChannelConfig,
} from './rpc-subscriptions-channel';
import type { RpcSubscriptionsFromTransport } from './rpc-subscriptions-clusters';
import { createDefaultRpcSubscriptionsTransport } from './rpc-subscriptions-transport';

type Config<TClusterUrl extends ClusterUrl> = DefaultRpcSubscriptionsChannelConfig<TClusterUrl>;

function createWikoRpcSubscriptionsImpl<TClusterUrl extends ClusterUrl, TApi extends RpcSubscriptionsApiMethods>(
    clusterUrl: TClusterUrl,
    config?: Omit<Config<TClusterUrl>, 'url'>,
) {
    const transport = createDefaultRpcSubscriptionsTransport({
        createChannel: createDefaultWikoRpcSubscriptionsChannelCreator({ ...config, url: clusterUrl }),
    });
    return createWikoRpcSubscriptionsFromTransport<typeof transport, TApi>(transport);
}

export function createWikoRpcSubscriptions<TClusterUrl extends ClusterUrl>(
    clusterUrl: TClusterUrl,
    config?: Omit<Config<TClusterUrl>, 'url'>,
) {
    return createWikoRpcSubscriptionsImpl<TClusterUrl, WikoRpcSubscriptionsApi>(clusterUrl, config);
}

export function createWikoRpcSubscriptions_UNSTABLE<TClusterUrl extends ClusterUrl>(
    clusterUrl: TClusterUrl,
    config?: Omit<Config<TClusterUrl>, 'url'>,
) {
    return createWikoRpcSubscriptionsImpl<TClusterUrl, WikoRpcSubscriptionsApi & WikoRpcSubscriptionsApiUnstable>(
        clusterUrl,
        config,
    );
}

export function createWikoRpcSubscriptionsFromTransport<
    TTransport extends RpcSubscriptionsTransport,
    TApi extends RpcSubscriptionsApiMethods = WikoRpcSubscriptionsApi,
>(transport: TTransport) {
    return createSubscriptionRpc({
        api: createWikoRpcSubscriptionsApi<TApi>(DEFAULT_RPC_SUBSCRIPTIONS_CONFIG),
        transport,
    }) as RpcSubscriptionsFromTransport<TApi, TTransport>;
}
